import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  useGetAdminContestsQuery,
  useCreateContestMutation,
  useUpdateContestMutation,
  useRegenerateContestApiKeyMutation,
  useDeleteContestMutation,
} from "../../features/api/appApi";
import AdminSectionCard, {
  AdminButton,
  AdminEmptyState,
  AdminField,
  AdminFormGrid,
  AdminInput,
  AdminSelect,
  AdminStatusBadge,
  AdminStatCard,
  AdminTextarea,
  adminCn,
} from "./AdminSectionCard";

const STATUS_TONE = {
  draft: "slate",
  published: "green",
  closed: "amber",
  archived: "red",
};

const defaultForm = {
  title: "",
  slug: "",
  description: "",
  type: "individual",
  minTeamSize: 2,
  maxTeamSize: 2,
  requiresPayment: false,
  requiresTshirtSize: false,
  paymentInstructions: "",
  venue: "",
  contestDate: "",
  registrationStartAt: "",
  registrationDeadlineAt: "",
  registrationEnabled: true,
  editingEnabled: false,
  maxRegistrations: "",
  status: "draft",
};

const toDatetimeLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const fromDatetimeLocal = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const formatDeadline = (iso) => {
  if (!iso) return "No deadline set";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "No deadline set";
  const diffMs = d.getTime() - Date.now();
  const label = d.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  if (diffMs <= 0) return `${label} (passed)`;
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs / (1000 * 60 * 60)) % 24);
  if (days > 0) return `${label} (in ${days}d ${hours}h)`;
  const mins = Math.floor((diffMs / (1000 * 60)) % 60);
  return `${label} (in ${hours}h ${mins}m)`;
};

const contestToForm = (contest) => ({
  title: contest.title || "",
  slug: contest.slug || "",
  description: contest.description || "",
  type: contest.type || "individual",
  minTeamSize: contest.minTeamSize ?? 2,
  maxTeamSize: contest.maxTeamSize ?? 2,
  requiresPayment: !!contest.requiresPayment,
  requiresTshirtSize: !!contest.requiresTshirtSize,
  paymentInstructions: contest.paymentInstructions || "",
  venue: contest.venue || "",
  contestDate: toDatetimeLocal(contest.contestDate),
  registrationStartAt: toDatetimeLocal(contest.registrationStartAt),
  registrationDeadlineAt: toDatetimeLocal(contest.registrationDeadlineAt),
  registrationEnabled: contest.registrationEnabled !== false,
  editingEnabled: !!contest.editingEnabled,
  maxRegistrations: contest.maxRegistrations ?? "",
  status: contest.status || "draft",
});

const buildPayload = (form, { includeStatus }) => {
  const payload = {
    title: form.title.trim(),
    description: form.description.trim() || null,
    type: form.type,
    requiresPayment: form.requiresPayment,
    requiresTshirtSize: form.requiresTshirtSize,
    venue: form.venue.trim() || null,
    contestDate: fromDatetimeLocal(form.contestDate),
    registrationStartAt: fromDatetimeLocal(form.registrationStartAt),
    registrationDeadlineAt: fromDatetimeLocal(form.registrationDeadlineAt),
    registrationEnabled: form.registrationEnabled,
    editingEnabled: form.editingEnabled,
    maxRegistrations: form.maxRegistrations === "" ? null : Number(form.maxRegistrations),
  };

  if (form.slug.trim()) payload.slug = form.slug.trim();

  if (form.type === "team") {
    payload.minTeamSize = Number(form.minTeamSize) || 2;
    payload.maxTeamSize = Number(form.maxTeamSize) || payload.minTeamSize;
  }

  if (form.requiresPayment) {
    payload.paymentInstructions = form.paymentInstructions.trim() || undefined;
  }

  if (includeStatus) payload.status = form.status;

  return payload;
};

const validateDateOrdering = (form) => {
  const start = fromDatetimeLocal(form.registrationStartAt);
  const deadline = fromDatetimeLocal(form.registrationDeadlineAt);
  const contestDate = fromDatetimeLocal(form.contestDate);

  if (start && deadline && new Date(deadline) <= new Date(start)) {
    return { field: "registrationDeadlineAt", message: "Must be after the registration start date." };
  }
  if (deadline && contestDate && new Date(contestDate) <= new Date(deadline)) {
    return { field: "contestDate", message: "Must be after the registration deadline." };
  }
  if (!deadline && start && contestDate && new Date(contestDate) <= new Date(start)) {
    return { field: "contestDate", message: "Must be after the registration start date." };
  }
  return null;
};

const errorInputClass = "border-rose-400 focus:border-rose-500 focus:ring-rose-100";

function FieldError({ message }) {
  if (!message) return null;
  return <p className="mt-1 text-xs font-semibold text-rose-600">{message}</p>;
}

const AdminContestsPage = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: contests = [], isFetching } = useGetAdminContestsQuery({
    search,
    status: statusFilter,
    type: typeFilter,
  });

  const [createContest] = useCreateContestMutation();
  const [updateContest] = useUpdateContestMutation();
  const [regenerateContestApiKey] = useRegenerateContestApiKeyMutation();
  const [deleteContest] = useDeleteContestMutation();

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(""), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const [modalMode, setModalMode] = useState(null);
  const [editingContest, setEditingContest] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const stats = useMemo(() => {
    return {
      total: contests.length,
      published: contests.filter((c) => c.status === "published").length,
      draft: contests.filter((c) => c.status === "draft").length,
      registrations: contests.reduce((sum, c) => sum + (c.registrationCount || 0), 0),
    };
  }, [contests]);

  const openCreateModal = () => {
    setForm(defaultForm);
    setEditingContest(null);
    setModalMode("create");
    setError("");
    setFieldErrors({});
  };

  const openEditModal = (contest) => {
    setForm(contestToForm(contest));
    setEditingContest(contest);
    setModalMode("edit");
    setError("");
    setFieldErrors({});
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingContest(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    const nextFieldErrors = {};
    if (!form.title.trim()) {
      nextFieldErrors.title = "Title is required.";
    }
    if (form.type === "team" && Number(form.maxTeamSize) < Number(form.minTeamSize)) {
      nextFieldErrors.maxTeamSize = "Must be greater than or equal to min team size.";
    }
    const dateError = validateDateOrdering(form);
    if (dateError) {
      nextFieldErrors[dateError.field] = dateError.message;
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      return;
    }

    setSaving(true);
    try {
      if (modalMode === "create") {
        const payload = buildPayload(form, { includeStatus: false });
        await createContest(payload).unwrap();
        setSuccess("Contest created successfully.");
      } else if (modalMode === "edit" && editingContest) {
        const payload = buildPayload(form, { includeStatus: true });
        await updateContest({ id: editingContest.id, ...payload }).unwrap();
        setSuccess("Contest updated successfully.");
      }
      closeModal();
    } catch (err) {
      setError(err?.data?.message || err?.message || "Failed to save contest.");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublish = async (contest) => {
    setError("");
    setBusyId(contest.id);
    try {
      const nextStatus = contest.status === "published" ? "draft" : "published";
      await updateContest({ id: contest.id, status: nextStatus }).unwrap();
      setSuccess(
        nextStatus === "published"
          ? `"${contest.title}" is now published and publicly visible.`
          : `"${contest.title}" was moved back to draft.`
      );
    } catch (err) {
      setError(err?.data?.message || err?.message || "Failed to change contest status.");
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleRegistration = async (contest) => {
    setError("");
    setBusyId(contest.id);
    try {
      await updateContest({
        id: contest.id,
        registrationEnabled: !contest.registrationEnabled,
      }).unwrap();
      setSuccess(
        !contest.registrationEnabled
          ? `Registration turned ON for "${contest.title}".`
          : `Registration turned OFF for "${contest.title}".`
      );
    } catch (err) {
      setError(err?.data?.message || err?.message || "Failed to toggle registration.");
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleEditing = async (contest) => {
    setError("");
    setBusyId(contest.id);
    try {
      await updateContest({
        id: contest.id,
        editingEnabled: !contest.editingEnabled,
      }).unwrap();
      setSuccess(
        !contest.editingEnabled
          ? `Participant self-edit unlocked for "${contest.title}".`
          : `Participant self-edit locked for "${contest.title}".`
      );
    } catch (err) {
      setError(err?.data?.message || err?.message || "Failed to toggle participant self-edit.");
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (contest) => {
    const confirmed = await Swal.fire({
      icon: "warning",
      title: "This is a risky admin action",
      html: `You are about to permanently delete <b>"${contest.title}"</b>. This cannot be undone.<br/><br/>Type the contest slug <code>${contest.slug}</code> below to confirm.`,
      input: "text",
      inputPlaceholder: contest.slug,
      inputAttributes: { autocapitalize: "off", autocorrect: "off", spellcheck: "false" },
      showCancelButton: true,
      confirmButtonText: "Delete contest",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      focusCancel: true,
      inputValidator: (value) =>
        value === contest.slug ? undefined : `Type "${contest.slug}" exactly to confirm deletion`,
    });
    if (!confirmed.isConfirmed) return;

    setError("");
    setBusyId(contest.id);
    try {
      await deleteContest({ id: contest.id }).unwrap();
      setSuccess(`"${contest.title}" was deleted.`);
    } catch (err) {
      const message = err?.data?.message || err?.message || "Failed to delete contest.";
      if (message.toLowerCase().includes("registration")) {
        const forceConfirm = await Swal.fire({
          icon: "error",
          title: "This is a risky admin action",
          html: `${message}<br/><br/>Do you want to <b>force-delete</b> this contest and ALL of its registrations? This cannot be undone.<br/><br/>Type the contest slug <code>${contest.slug}</code> below to confirm.`,
          input: "text",
          inputPlaceholder: contest.slug,
          inputAttributes: { autocapitalize: "off", autocorrect: "off", spellcheck: "false" },
          showCancelButton: true,
          confirmButtonText: "Force delete everything",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#dc2626",
          cancelButtonColor: "#64748b",
          focusCancel: true,
          inputValidator: (value) =>
            value === contest.slug ? undefined : `Type "${contest.slug}" exactly to confirm deletion`,
        });
        if (forceConfirm.isConfirmed) {
          try {
            await deleteContest({ id: contest.id, force: true }).unwrap();
            setSuccess(`"${contest.title}" and its registrations were deleted.`);
          } catch (err2) {
            setError(err2?.data?.message || err2?.message || "Failed to force-delete contest.");
          }
        }
      } else {
        setError(message);
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleRegenerateKey = async (contest) => {
    const confirmed = await Swal.fire({
      icon: "warning",
      title: "This is a risky admin action",
      text: "Regenerating the API key will immediately invalidate the old key. Any custom registration site using the old key will stop working until it's updated. Continue?",
      showCancelButton: true,
      confirmButtonText: "Yes, regenerate it",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      focusCancel: true,
    });
    if (!confirmed.isConfirmed) return;

    setError("");
    setBusyId(contest.id);
    try {
      const res = await regenerateContestApiKey(contest.id).unwrap();
      setSuccess("API key regenerated.");
      if (editingContest?.id === contest.id) {
        setEditingContest(res.contest);
      }
    } catch (err) {
      setError(err?.data?.message || err?.message || "Failed to regenerate API key.");
    } finally {
      setBusyId(null);
    }
  };

  const handleCopyKey = async (contest) => {
    try {
      await navigator.clipboard.writeText(contest.apiKey || "");
      setCopiedId(contest.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      setError("Failed to copy API key to clipboard.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <AdminStatCard title="Total Contests" value={stats.total} tone="slate" />
        <AdminStatCard title="Published" value={stats.published} tone="green" />
        <AdminStatCard title="Draft" value={stats.draft} tone="amber" />
        <AdminStatCard title="Total Registrations" value={stats.registrations} tone="blue" />
      </div>

      {success ? (
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800 font-medium">
          {success}
        </div>
      ) : null}

      {error ? (
        <div className="rounded-xl bg-rose-50 border border-rose-200 p-4 text-sm text-rose-800 font-medium">
          {error}
        </div>
      ) : null}

      <AdminSectionCard
        title="Contests"
        subtitle="Create and manage dynamic contests. Only published contests are visible to the public."
        rightAction={<AdminButton onClick={openCreateModal}>+ New Contest</AdminButton>}
      >
        <div className="mb-5 flex flex-col gap-3 sm:flex-row">
          <input
            type="text"
            placeholder="Search by title or slug..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
          />
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="closed">Closed</option>
              <option value="archived">Archived</option>
            </select>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700 outline-none focus:border-slate-400"
            >
              <option value="">All types</option>
              <option value="individual">Individual</option>
              <option value="team">Team</option>
            </select>
          </div>
        </div>

        {isFetching ? (
          <div className="py-12 text-center text-sm font-semibold text-slate-500">Loading contests...</div>
        ) : contests.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {contests.map((contest) => (
              <div
                key={contest.id}
                className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="break-words text-base font-bold text-slate-900">{contest.title}</h3>
                    <p className="mt-0.5 truncate text-xs text-slate-400">/{contest.slug}</p>
                  </div>
                  <AdminStatusBadge tone={STATUS_TONE[contest.status] || "slate"}>
                    {contest.status}
                  </AdminStatusBadge>
                </div>

                <div className="flex flex-wrap gap-2">
                  <AdminStatusBadge tone="blue">
                    {contest.type === "team"
                      ? `Team (${contest.minTeamSize ?? "?"}-${contest.maxTeamSize ?? "?"})`
                      : "Individual"}
                  </AdminStatusBadge>
                  {contest.requiresPayment ? (
                    <AdminStatusBadge tone="amber">Payment required</AdminStatusBadge>
                  ) : null}
                  {contest.requiresTshirtSize ? (
                    <AdminStatusBadge tone="green">T-shirt size required</AdminStatusBadge>
                  ) : null}
                </div>

                <div className="space-y-1 text-xs text-slate-500">
                  <p>
                    <span className="font-semibold text-slate-600">Registrations:</span>{" "}
                    {contest.registrationCount ?? 0}
                    {contest.maxRegistrations ? ` / ${contest.maxRegistrations}` : ""}
                  </p>
                  <p>
                    <span className="font-semibold text-slate-600">Deadline:</span>{" "}
                    {formatDeadline(contest.registrationDeadlineAt)}
                  </p>
                  <p
                    className={adminCn(
                      "font-semibold",
                      contest.isRegistrationOpen ? "text-emerald-600" : "text-slate-500"
                    )}
                    title={!contest.isRegistrationOpen ? contest.registrationClosedReason || "" : ""}
                  >
                    Registration: {contest.isRegistrationOpen ? "Open" : "Closed"}
                    {!contest.isRegistrationOpen && contest.registrationClosedReason
                      ? ` — ${contest.registrationClosedReason}`
                      : ""}
                  </p>
                </div>

                <div className="mt-1 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  <AdminButton
                    className="h-9 px-3 text-xs"
                    variant="ghost"
                    onClick={() => openEditModal(contest)}
                  >
                    Edit
                  </AdminButton>
                  <Link to={`/admin/contests/${contest.id}`}>
                    <AdminButton className="h-9 px-3 text-xs" variant="secondary">
                      Manage Registrations
                    </AdminButton>
                  </Link>
                  <Link to={`/admin/contests/${contest.id}/participants`}>
                    <AdminButton className="h-9 px-3 text-xs" variant="ghost">
                      Participants
                    </AdminButton>
                  </Link>
                  <AdminButton
                    className="h-9 px-3 text-xs"
                    variant={contest.status === "published" ? "ghost" : "success"}
                    disabled={busyId === contest.id}
                    onClick={() => handleTogglePublish(contest)}
                  >
                    {contest.status === "published" ? "Unpublish" : "Publish"}
                  </AdminButton>
                  {contest.status === "published" ? (
                    <AdminButton
                      className="h-9 px-3 text-xs"
                      variant={contest.registrationEnabled ? "success" : "ghost"}
                      disabled={busyId === contest.id}
                      onClick={() => handleToggleRegistration(contest)}
                    >
                      Registration: {contest.registrationEnabled ? "ON" : "OFF"}
                    </AdminButton>
                  ) : null}
                  <AdminButton
                    className="h-9 px-3 text-xs"
                    variant={contest.editingEnabled ? "success" : "ghost"}
                    disabled={busyId === contest.id}
                    onClick={() => handleToggleEditing(contest)}
                    title="Let participants edit their name, team name & T-shirt size on the public site"
                  >
                    Self-edit: {contest.editingEnabled ? "ON" : "OFF"}
                  </AdminButton>
                  <AdminButton
                    className="h-9 px-3 text-xs"
                    variant="danger"
                    disabled={busyId === contest.id}
                    onClick={() => handleDelete(contest)}
                  >
                    Delete
                  </AdminButton>
                </div>

                <div className="rounded-xl bg-slate-50 p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">API Key</p>
                  <div className="mt-1 flex items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded-lg bg-white px-2 py-1.5 font-mono text-xs text-slate-700 ring-1 ring-slate-200">
                      {contest.apiKey}
                    </code>
                    <button
                      type="button"
                      onClick={() => handleCopyKey(contest)}
                      className="shrink-0 rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                    >
                      {copiedId === contest.id ? "Copied!" : "Copy"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <AdminEmptyState text="No contests found. Create one to get started." />
        )}
      </AdminSectionCard>

      {modalMode ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h3 className="text-lg font-bold text-slate-800">
                {modalMode === "create" ? "Create Contest" : `Edit: ${editingContest?.title}`}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="text-lg text-slate-400 transition-colors hover:text-slate-600"
              >
                ✕
              </button>
            </div>

            <form className="mt-5 space-y-4" onSubmit={handleSubmit}>
              <AdminFormGrid>
                <AdminField label="Title" className="md:col-span-2">
                  <AdminInput
                    value={form.title}
                    onChange={(e) => {
                      setForm({ ...form, title: e.target.value });
                      if (fieldErrors.title) setFieldErrors((prev) => ({ ...prev, title: "" }));
                    }}
                    className={fieldErrors.title ? errorInputClass : ""}
                  />
                  <FieldError message={fieldErrors.title} />
                </AdminField>

                <AdminField
                  label={
                    modalMode === "edit" && editingContest?.registrationCount > 0
                      ? "Slug (locked — registrations have started)"
                      : "Slug (optional, auto-generated if left blank)"
                  }
                >
                  <AdminInput
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="e.g. summer-coding-cup-2026"
                    disabled={modalMode === "edit" && editingContest?.registrationCount > 0}
                  />
                </AdminField>

                <AdminField label="Type">
                  <AdminSelect
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  >
                    <option value="individual">Individual</option>
                    <option value="team">Team</option>
                  </AdminSelect>
                </AdminField>

                <AdminField label="Description" className="md:col-span-2">
                  <AdminTextarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </AdminField>

                {form.type === "team" ? (
                  <>
                    <AdminField label="Min team size">
                      <AdminInput
                        type="number"
                        min={1}
                        value={form.minTeamSize}
                        onChange={(e) => setForm({ ...form, minTeamSize: e.target.value })}
                      />
                    </AdminField>
                    <AdminField label="Max team size">
                      <AdminInput
                        type="number"
                        min={1}
                        value={form.maxTeamSize}
                        onChange={(e) => {
                          setForm({ ...form, maxTeamSize: e.target.value });
                          if (fieldErrors.maxTeamSize) setFieldErrors((prev) => ({ ...prev, maxTeamSize: "" }));
                        }}
                        className={fieldErrors.maxTeamSize ? errorInputClass : ""}
                      />
                      <FieldError message={fieldErrors.maxTeamSize} />
                    </AdminField>
                  </>
                ) : null}

                <AdminField label="Venue">
                  <AdminInput
                    value={form.venue}
                    onChange={(e) => setForm({ ...form, venue: e.target.value })}
                  />
                </AdminField>

                <AdminField label="Contest date">
                  <AdminInput
                    type="datetime-local"
                    value={form.contestDate}
                    onChange={(e) => {
                      setForm({ ...form, contestDate: e.target.value });
                      if (fieldErrors.contestDate) setFieldErrors((prev) => ({ ...prev, contestDate: "" }));
                    }}
                    className={fieldErrors.contestDate ? errorInputClass : ""}
                  />
                  <FieldError message={fieldErrors.contestDate} />
                </AdminField>

                <AdminField label="Registration start">
                  <AdminInput
                    type="datetime-local"
                    value={form.registrationStartAt}
                    onChange={(e) => {
                      setForm({ ...form, registrationStartAt: e.target.value });
                      if (fieldErrors.registrationDeadlineAt) setFieldErrors((prev) => ({ ...prev, registrationDeadlineAt: "" }));
                    }}
                  />
                </AdminField>

                <AdminField label="Registration deadline">
                  <AdminInput
                    type="datetime-local"
                    value={form.registrationDeadlineAt}
                    onChange={(e) => {
                      setForm({ ...form, registrationDeadlineAt: e.target.value });
                      if (fieldErrors.registrationDeadlineAt) setFieldErrors((prev) => ({ ...prev, registrationDeadlineAt: "" }));
                    }}
                    className={fieldErrors.registrationDeadlineAt ? errorInputClass : ""}
                  />
                  <FieldError message={fieldErrors.registrationDeadlineAt} />
                </AdminField>

                <AdminField label="Max registrations (blank = unlimited)">
                  <AdminInput
                    type="number"
                    min={0}
                    value={form.maxRegistrations}
                    onChange={(e) => setForm({ ...form, maxRegistrations: e.target.value })}
                  />
                </AdminField>

                <AdminField label="Registration enabled">
                  <div className="flex h-11 items-center gap-2">
                    <input
                      type="checkbox"
                      id="registrationEnabled"
                      checked={form.registrationEnabled}
                      onChange={(e) =>
                        setForm({ ...form, registrationEnabled: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <label htmlFor="registrationEnabled" className="text-sm text-slate-600">
                      Allow new registrations
                    </label>
                  </div>
                </AdminField>

                <AdminField label="Allow participant self-edit">
                  <div className="flex h-11 items-center gap-2">
                    <input
                      type="checkbox"
                      id="editingEnabled"
                      checked={form.editingEnabled}
                      onChange={(e) => setForm({ ...form, editingEnabled: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <label htmlFor="editingEnabled" className="text-sm text-slate-600">
                      Participants can edit their name, team name &amp; T-shirt size
                    </label>
                  </div>
                </AdminField>

                <AdminField label="Requires payment">
                  <div className="flex h-11 items-center gap-2">
                    <input
                      type="checkbox"
                      id="requiresPayment"
                      checked={form.requiresPayment}
                      onChange={(e) => setForm({ ...form, requiresPayment: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <label htmlFor="requiresPayment" className="text-sm text-slate-600">
                      Registrants must submit payment info
                    </label>
                  </div>
                </AdminField>

                <AdminField label="Requires T-shirt size">
                  <div className="flex h-11 items-center gap-2">
                    <input
                      type="checkbox"
                      id="requiresTshirtSize"
                      checked={form.requiresTshirtSize}
                      onChange={(e) => setForm({ ...form, requiresTshirtSize: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300"
                    />
                    <label htmlFor="requiresTshirtSize" className="text-sm text-slate-600">
                      Registrants must provide a T-shirt size
                    </label>
                  </div>
                </AdminField>

                {form.requiresPayment ? (
                  <AdminField label="Payment instructions" className="md:col-span-2">
                    <AdminTextarea
                      placeholder="e.g. Send fee via bKash to 017xxxxxxxx"
                      value={form.paymentInstructions}
                      onChange={(e) =>
                        setForm({ ...form, paymentInstructions: e.target.value })
                      }
                    />
                  </AdminField>
                ) : null}

                {modalMode === "edit" ? (
                  <AdminField label="Status" className="md:col-span-2">
                    <AdminSelect
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="closed">Closed</option>
                      <option value="archived">Archived</option>
                    </AdminSelect>
                    <p className="mt-1 text-xs text-slate-400">
                      Only Published contests are visible to the public and accept registrations.
                    </p>
                  </AdminField>
                ) : null}
              </AdminFormGrid>

              {modalMode === "edit" && editingContest ? (
                <div className="rounded-xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    API Key
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Share this with any developer building a custom registration site for this
                    contest. Required in the <code className="font-mono">x-api-key</code> header
                    when POSTing to <code className="font-mono">/student/contests/{editingContest.slug}/register</code>.
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <code className="min-w-0 flex-1 truncate rounded-lg bg-white px-3 py-2 font-mono text-xs text-slate-700 ring-1 ring-slate-200">
                      {editingContest.apiKey}
                    </code>
                    <AdminButton
                      type="button"
                      variant="ghost"
                      className="h-9 px-3 text-xs"
                      onClick={() => handleCopyKey(editingContest)}
                    >
                      {copiedId === editingContest.id ? "Copied!" : "Copy"}
                    </AdminButton>
                    <AdminButton
                      type="button"
                      variant="danger"
                      className="h-9 px-3 text-xs"
                      disabled={busyId === editingContest.id || editingContest.registrationCount > 0}
                      title={
                        editingContest.registrationCount > 0
                          ? "Locked — registrations have started"
                          : undefined
                      }
                      onClick={() => handleRegenerateKey(editingContest)}
                    >
                      Regenerate
                    </AdminButton>
                  </div>
                  {editingContest.registrationCount > 0 ? (
                    <p className="mt-2 text-xs text-amber-600">
                      Slug and API key are locked because this contest already has registrations.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
                <AdminButton type="button" variant="secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </AdminButton>
                <AdminButton type="submit" disabled={saving}>
                  {saving ? "Saving..." : modalMode === "create" ? "Create Contest" : "Save Changes"}
                </AdminButton>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AdminContestsPage;
